import { useTranslation } from "react-i18next";
import type { EditorialContent } from "@nh/shared";
import { cn } from "@/shared/lib/cn";
import { ProductBlockHeading } from "./ProductBlockHeading";
import { ProductEditorialIcon } from "./ProductEditorialIcon";

export type ProductIngredientSection = NonNullable<EditorialContent["ingredients"]>;

export interface ProductIngredientsProps {
  section: ProductIngredientSection;
  number?: string;
}

/**
 * Los extractos clave, en tarjetas de tres en fondo.
 *
 * ── El tema lo manda el catálogo, y antes no ─────────────────────────
 * Esta pieza pintaba SIEMPRE en oscuro. `ingredients.theme` existe en el
 * contrato, el editorial real de Sexto Elemento trae `theme: "light"`, y
 * el original lo respeta: allí este bloque sale en tarjetas blancas.
 * Aquí salía en negro, y era la segunda diferencia más visible después
 * del estandarte — media pantalla de otro color.
 *
 * `"dark"` sigue siendo lo que se usa cuando no se dice nada, igual que
 * en el original (`ed.ingredients.theme || "dark"`).
 *
 * ── Medidas del original ─────────────────────────────────────────────
 *   rejilla   1 / 2 / 3 columnas · separación 12
 *   tarjeta   padding 22 · radio 3 (claro: borde `line`)
 *   icono     38px · radio 4 · 14 px de aire debajo
 *   spec      mono 9px · 0.12em · 6 px debajo
 *   nombre    17px/600 · -0.01em · 6 px debajo
 *   texto     12px/300 · interlínea 1.5
 *
 * ── Los dos colores que suben ────────────────────────────────────────
 * En la tarjeta OSCURA el original pinta el `spec` y el icono en
 * `violet-pop`, que sobre `ink` da 2.78:1 — un rótulo de 9 px que no se
 * puede leer. Ahí suben a `lilac` (#C7BFDF, 5.43:1). En la tarjeta CLARA
 * se quedan en `violet-pop`, que sobre blanco da 5.96:1 y es literal.
 */
export function ProductIngredients({ section, number }: ProductIngredientsProps) {
  const { t } = useTranslation();
  const dark = (section.theme ?? "dark") === "dark";

  return (
    <section
      className={cn(
        "grid gap-8 rounded-[32px] p-6 sm:p-10",
        dark ? "bg-gradient-to-br from-ds-ink via-ds-ink-2 to-ds-indigo-night text-white" : "border border-ds-border bg-white",
      )}
    >
      <ProductBlockHeading
        tone={dark ? "dark" : "light"}
        eyebrow={t("Ingredientes")}
        number={number}
        title={section.title}
        intro={section.intro}
      />

      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {section.items.map((ingredient) => (
          <li
            key={ingredient.name}
            className={cn(
              "rounded-[24px] p-[22px] transition",
              dark
                ? "border border-white/10 bg-white/[0.06] backdrop-blur-sm hover:bg-white/[0.1]"
                : "border border-ds-border bg-ds-cream-2/40 hover:bg-white",
            )}
          >
            <ProductEditorialIcon
              name={ingredient.icon}
              variant={dark ? "ingredient" : "ingredient-light"}
            />

            {ingredient.spec && (
              <p
                className={cn(
                  "mt-3.5 font-mono text-[9px] uppercase leading-[1.4] tracking-[0.12em]",
                  dark ? "text-ds-lilac" : "text-ds-violet-pop",
                )}
              >
                {ingredient.spec}
              </p>
            )}

            <h3
              className={cn(
                "mt-1.5 text-[17px] font-semibold leading-[1.25] tracking-[-0.01em]",
                dark ? "text-white" : "text-ds-ink",
              )}
            >
              {ingredient.name}
            </h3>

            {ingredient.description && (
              <p
                className={cn(
                  "mt-1.5 text-[12px] font-light leading-[1.5]",
                  dark ? "text-ds-lilac" : "text-ds-ink-3",
                )}
              >
                {ingredient.description}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
